import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, ShieldCheck, History, SpellCheck, Wand2, Headphones, Layers } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Wordmark, TaglineBar } from "../components/Brand";
import { FormatsSection } from "../components/FormatsSection";
import { PricingGuide } from "../components/PricingGuide";

const HERO = "https://static.prod-images.emergentagent.com/jobs/1d89f7cd-f952-4882-8e9a-96e96a05855e/images/f6047d453d29ce36c3f79cb1a2feffd1a0688444ab028e3509dfd369b9c3245e.jpeg";

const FEATURES = [
  { icon: History, title: "Nothing is ever overwritten", text: "Every upload is stored immutably. Every edit becomes a verified version you can compare and restore." },
  { icon: SpellCheck, title: "Proofreading that respects your voice", text: "Four review modes. Each correction shows original, replacement and reason — accept or reject one by one." },
  { icon: Wand2, title: "Controlled rewriting & expansion", text: "Approve what must be preserved first. Then rewrite chapter by chapter with checkpoints and word-goal reports." },
  { icon: Layers, title: "Series continuity", text: "A canon reference for characters, places and timelines across every book in a series — and box sets in one click." },
  { icon: Headphones, title: "Four editions, one source", text: "Kindle ebook, paperback, hardcover and audiobook workflows that each point to an approved version." },
  { icon: ShieldCheck, title: "Honest preflight", text: "PASS, WARNING, ERROR or NOT VERIFIED — an unperformed check is never shown as passed." },
];

const hex = "[clip-path:polygon(25%_0,75%_0,100%_50%,75%_100%,25%_100%,0_50%)]";

function Hero({ cta }) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero">
      <img src={HERO} alt="Gothic library with a glowing open book and quill beneath a violet moon" className="absolute inset-0 w-full h-full object-cover opacity-80" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/40" />
      <div className="smoke" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-28 pb-20 w-full grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 flex flex-col items-center">
          <div className="emblem-ring w-72 h-72 sm:w-96 sm:h-96 flex items-center justify-center bg-black/45 backdrop-blur-sm fade-up">
            <span className="spark -top-[3px] left-1/2 -translate-x-1/2" /><span className="spark -bottom-[3px] left-1/2 -translate-x-1/2" />
            <Wordmark size="lg" testId="hero-wordmark" />
          </div>
          <div className="mt-8 fade-up" style={{ animationDelay: ".15s" }}><TaglineBar /></div>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <p className="eyebrow mb-6 fade-up">Books · comics · manga · webtoons · poetry</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] fade-up" style={{ animationDelay: ".1s" }}>Forge books, comics and poetry — and never lose a word.</h1>
          <p className="text-base md:text-lg text-slate-300 max-w-xl mt-8 fade-up" style={{ animationDelay: ".2s" }}>
            Write, illustrate and publish novels, comics, manga, webtoons and poems in one studio — with AI proofreading, panel art, audio readings and a traceable version history. Movies and games are coming in future expansions.
          </p>
          <div className="flex flex-wrap gap-4 mt-10 fade-up" style={{ animationDelay: ".3s" }}>
            <Link to={cta} className="glow-btn inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-6 py-3 rounded-lg font-medium border border-amber-400/30" data-testid="hero-cta">Enter the studio <ArrowRight size={16} /></Link>
            <a href="#features" className="inline-flex items-center px-6 py-3 rounded-lg border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 transition-colors" data-testid="hero-features-link">How it works</a>
            <a href="#formats" className="inline-flex items-center px-6 py-3 rounded-lg text-amber-200 hover:text-white transition-colors" data-testid="hero-formats-link">See formats</a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/app" : "/login";
  return (
    <div className="relative z-10">
      <header className="fixed top-0 inset-x-0 z-50 bg-slate-950/70 backdrop-blur-xl border-b border-amber-500/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-brand"><BookOpen className="text-amber-400" size={20} /><Wordmark size="sm" tagline={false} testId="header-wordmark" /></Link>
          <div className="flex items-center gap-4"><Link to="/authors" className="text-sm text-amber-200 hover:text-white" data-testid="header-books-link">Books</Link>
          <Link to={cta} className="glow-btn bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium px-4 py-2 rounded-lg border border-amber-400/30" data-testid="header-cta">{user ? "Open studio" : "Sign in"}</Link></div>
        </div>
      </header>
      <Hero cta={cta} />
      <FormatsSection />
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-4">Integrity first</p>
            <h2 className="font-display text-3xl lg:text-4xl leading-tight">Built for authors who cannot afford to lose a single scene.</h2>
            <p className="text-slate-400 mt-6 text-sm leading-relaxed">AI features are processed by external providers and billed separately from membership. The studio tracks every request and enforces your spending limit.</p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-amber-900/40 border border-amber-500/20 rounded-xl overflow-hidden">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-slate-900 p-8 hover:bg-slate-800/80 transition-colors" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
                <div className={`w-11 h-11 mb-5 flex items-center justify-center bg-amber-800/40 ${hex}`}><f.icon className="text-amber-200" size={19} /></div>
                <h3 className="font-display text-lg font-semibold text-slate-100 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-8 pb-24" data-testid="pricing-section">
        <p className="eyebrow mb-4">What AI costs</p>
        <h2 className="font-display text-3xl lg:text-4xl leading-tight mb-10 max-w-2xl">Know your credit spend before you start.</h2>
        <PricingGuide />
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
        <div className="relative overflow-hidden rounded-2xl border border-amber-500/25 shadow-[0_0_40px_rgba(157,78,221,0.15)]">
          <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" /><div className="smoke" />
          <div className="relative p-10 sm:p-16 grid md:grid-cols-2 gap-8 items-center">
            <h2 className="font-display text-3xl lg:text-4xl">Private by default. Registration opens only when the owner decides.</h2>
            <div className="md:text-right"><Link to={cta} className="glow-btn inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-6 py-3 rounded-lg font-medium" data-testid="footer-cta">Sign in <ArrowRight size={16} /></Link></div>
          </div>
        </div>
      </section>
      <footer className="border-t border-amber-500/15 py-8 text-center text-xs text-slate-500">Khumgetme Media · Production Studio · Validation reports never guarantee acceptance by KDP or any distributor.</footer>
    </div>
  );
}
